/* eslint-disable @typescript-eslint/naming-convention */
import { PLACEHOLDERS } from "@super-ui/constants";
import { CURLY_BRACES, PRE_CONFIGURED_FILLERS, STRIKE_TAG } from "@super-ui/constants/SwiggyOne";
import { PlanAttributeValues } from "@swiggy-private/cart-types";
import { isEmpty, replaceAllOccurence } from "@swiggy-private/core-ui";

export const formatForA11y: (title: string, subtitle: string, strMap: Record<string, number>) => string = (
    title,
    subtitle,
    strMap,
) => {
    const replaceableKeys = Object.keys(strMap);

    let output = `${title} ${subtitle}`;

    for (const key of replaceableKeys) {
        const searchString = `{{${key}}}`;
        let valueToFill = String(strMap[key]);

        if (key === PRE_CONFIGURED_FILLERS.SUB_DISCOUNT || key === PRE_CONFIGURED_FILLERS.TOTAL_DISCOUNT) {
            valueToFill += " rupees";
        }
        output = output.replace(new RegExp(searchString, "g"), valueToFill);
    }

    return output;
};

export const getPrefixedTestId = (text: string, prefix: string | undefined): string => {
    if (isEmpty(prefix)) {
        return text;
    }
    return `${prefix}-${text}`;
};

export const getFormattedNumber = (num: number): string => {
    return new Intl.NumberFormat("en-IN").format(num);
};

export const getNumberOfDigits = (num: number, isUx4: boolean): number => {
    if (isUx4) {
        if (num < 10) return 1;
        if (num < 100) return 2;
        if (num < 200) return 3;
        if (num < 1000) return 4;
        if (num < 1100) return 5;
        if (num < 1200) return 6;
        if (num < 2000) return 5;
        if (num < 10000) return 7;
        return 8;
    }

    if (num < 10) return 1;
    if (num < 100) return 2;
    if (num < 1000) return 3;
    if (num < 10000) return 4;
    return 5;
};

export const getBoldTitle = (title: string, startPattern: string, endPattern: string): string => {
    let boldTitle = replaceAllOccurence(title, startPattern, '<span style="font-weight: 600;">');
    boldTitle = replaceAllOccurence(boldTitle, endPattern, "</span>");
    return boldTitle;
};

/**
 * Replaces the text enclosed in curly braces with strike-through tags.
 * If attribute_values are provided, it replaces the text within the curly braces with the corresponding attribute value.
 *
 * @param text - The input text to be processed.
 * @param attribute_values - Optional object containing attribute values.
 * @returns The formatted text with strike-through tags.
 */
export const getStrickedText = (text: string, attribute_values?: PlanAttributeValues): string => {
    if (isEmpty(text)) {
        return "";
    }
    if (text?.includes(CURLY_BRACES.OPEN) && text?.includes(CURLY_BRACES.CLOSE)) {
        let strickedText = replaceAllOccurence(text, CURLY_BRACES.OPEN, STRIKE_TAG.OPEN);
        strickedText = replaceAllOccurence(strickedText, CURLY_BRACES.CLOSE, STRIKE_TAG.CLOSE);
        const splittedText = strickedText.split(" ");

        const formattedText = splittedText
            ?.map(word => {
                if (word?.includes(STRIKE_TAG.OPEN) && word?.includes(STRIKE_TAG.CLOSE)) {
                    const keyToBeReplaced = word?.replace(STRIKE_TAG.OPEN, "")?.replace(STRIKE_TAG.CLOSE, "");
                    const replaceableText = attribute_values?.[keyToBeReplaced];
                    return !isEmpty(replaceableText) ? `${STRIKE_TAG.OPEN}${replaceableText}${STRIKE_TAG.CLOSE}` : "";
                }
                return word;
            })
            .filter(Boolean);

        return formattedText.join(" ");
    }
    return text;
};

export const imageConfigFormatter = (imgString: string) => {
    // width|height|cloudinaryId
    // width and height need to be "__px" strings
    const [width, height, cloudinaryIdPostText] = imgString.split("|");
    const cloudinaryId = cloudinaryIdPostText
        ?.replace(PLACEHOLDERS.INLINE_IMG_START, "")
        .replace(PLACEHOLDERS.INLINE_IMG_END, "");
    return {
        width,
        height,
        cloudinaryId,
    };
};
